import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { apiUrl } from '../utils/api';
import AuthSidebar from './AuthSidebar';
import engazeiqLogo from '../engazeiq_logo.png';
import { Eye, EyeOff } from 'lucide-react';

// --- Styles ---
const LoginContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  background-color: var(--secondary-color);
`;

const LoginMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: white;
`;

const LoginForm = styled.div`
  width: 100%;
  max-width: 400px;
  border-radius: 12px;
  padding: 32px;
  background-color: white;
  text-align: center;
`;

const LoginHeader = styled.h2`
  font-size: 24px;
  margin-bottom: 24px;
  font-weight: 200;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-color, #ccc);
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    border-color: var(--accent-color, #FF8000);
    outline: none;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background-color: var(--accent-color, #FF8000);
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover:not(:disabled) {
    background-color: #e67e00;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
  padding: 8px;
  background-color: rgba(211, 47, 47, 0.1);
  border-radius: 4px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #388e3c;
  font-size: 14px;
  margin-top: 8px;
  padding: 8px;
  background-color: rgba(56, 142, 60, 0.1);
  border-radius: 4px;
  text-align: center;
`;

const PasswordWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  
  &:hover {
    color: #333;
  }
`;

const PasswordStrengthMeter = styled.div<{ strength: number }>`
  margin-top: 8px;
  height: 4px;
  background-color: #e0e0e0;
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    height: 100%;
    background-color: ${(props) => {
      if (!props.strength) return 'transparent';
      if (props.strength < 2) return '#d32f2f';
      if (props.strength < 3) return '#f57c00';
      return '#388e3c';
    }};
    width: ${(props) => Math.min((props.strength / 4) * 100, 100)}%;
    transition: width 0.3s ease;
  }
`;

const PasswordStrengthText = styled.div<{ strength: number }>`
  font-size: 12px;
  margin-top: 4px;
  color: ${(props) => {
    if (!props.strength) return '#666';
    if (props.strength < 2) return '#d32f2f';
    if (props.strength < 3) return '#f57c00';
    return '#388e3c';
  }};
`;

const LinkButton = styled.a`
  color: #FF8000;
  text-decoration: none;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

// --- Component ---
const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Check password strength
  const calculatePasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    return strength;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setNewPassword(password);
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token || !email) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const res = await fetch(apiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          email,
          new_password: newPassword,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to reset password');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <LoginContainer>
        <AuthSidebar />
        <LoginMain>
          <LoginForm>
            <img src={engazeiqLogo} alt="EngazeIQ Logo" style={{ height: '40px', marginBottom: '24px' }} />
            <LoginHeader>Password Reset Successful</LoginHeader>
            <SuccessMessage>
              Your password has been reset successfully. Redirecting to login...
            </SuccessMessage>
            <div style={{ marginTop: '24px', color: '#666', fontSize: '14px' }}>
              <p>
                If not redirected automatically, click{' '}
                <LinkButton onClick={() => navigate('/login')}>here</LinkButton>
              </p>
            </div>
          </LoginForm>
        </LoginMain>
      </LoginContainer>
    );
  }

  if (!token || !email) {
    return (
      <LoginContainer>
        <AuthSidebar />
        <LoginMain>
          <LoginForm>
            <img src={engazeiqLogo} alt="EngazeIQ Logo" style={{ height: '40px', marginBottom: '24px' }} />
            <LoginHeader>Invalid Link</LoginHeader>
            <ErrorMessage>
              This password reset link is invalid or has expired. Please request a new one.
            </ErrorMessage>
            <div style={{ marginTop: '24px' }}>
              <LinkButton onClick={() => navigate('/login')}>← Back to Login</LinkButton>
            </div>
          </LoginForm>
        </LoginMain>
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      <AuthSidebar />
      <LoginMain>
        <LoginForm>
          <img src={engazeiqLogo} alt="EngazeIQ Logo" style={{ height: '40px', marginBottom: '24px' }} />
          <LoginHeader>Reset Password</LoginHeader>

          <form onSubmit={handleResetPassword}>
            <InputGroup>
              <PasswordWrapper>
                <TextInput
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={handlePasswordChange}
                  placeholder="New password"
                  required
                  disabled={isLoading}
                  style={{ paddingRight: "40px" }}
                />
                <ToggleButton
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </ToggleButton>
              </PasswordWrapper>
              <PasswordStrengthMeter strength={passwordStrength} />
              <PasswordStrengthText strength={passwordStrength}>
                {passwordStrength === 0 && 'Enter a password'}
                {passwordStrength === 1 && '⚠️ Weak password'}
                {passwordStrength === 2 && '⚠️ Fair password'}
                {passwordStrength === 3 && '✓ Good password'}
                {passwordStrength === 4 && '✓ Strong password'}
              </PasswordStrengthText>
            </InputGroup>

            <InputGroup>
              <PasswordWrapper>
                <TextInput
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  required
                  disabled={isLoading}
                  style={{ paddingRight: "40px" }}
                />
                <ToggleButton
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </ToggleButton>
              </PasswordWrapper>
            </InputGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <LoginButton type="submit" disabled={isLoading || !newPassword || !confirmPassword}>
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </LoginButton>
          </form>

          <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
            <p>
              Remember your password?{' '}
              <LinkButton onClick={() => navigate('/login')}>Sign in</LinkButton>
            </p>
          </div>
        </LoginForm>
      </LoginMain>
    </LoginContainer>
  );
};

export default ResetPassword;
